<template>
  <div class="lead-flow-workspace">
    <main class="lead-flow-workspace__main">
      <div class="lead-flow-workspace__overview">
        <slot name="overview"></slot>
      </div>

      <LeadFlowActions
        ref="flowActions"
        :lead-id="leadId"
        :communication-methods="communicationMethods"
        :refresh-key="flowRefreshKey"
        @flow-loaded="handleFlowLoaded"
        @action-complete="handleActionComplete"
        @action-conflict="handleActionConflict"
        @pending-change="setPending('actions', $event)"
      />

      <div
        v-if="communicationMethodsError"
        class="lead-flow-workspace__dictionary-error"
        role="alert"
      >
        Communication methods are unavailable. Action and manual communication forms may be limited.
        <button type="button" @click="loadCommunicationMethods">Retry</button>
      </div>

      <div class="lead-flow-workspace__operations">
        <LeadFlowTimeline
          ref="timeline"
          :lead-id="leadId"
          :refresh-key="timelineRefreshKey"
        />
        <LeadFollowUpAutomationPanel
          ref="followUpPanel"
          :lead-id="leadId"
          @board-reconcile="$emit('board-reconcile', $event)"
        />
        <LeadNotesPanel
          ref="notesPanel"
          :lead-id="leadId"
          @pending-change="setPending('notes', $event)"
        />
      </div>
    </main>

    <div class="lead-flow-workspace__side">
      <LeadCommunicationHistory
        ref="communicationHistory"
        class="lead-flow-workspace__communication"
        :lead-id="leadId"
        :communication-methods="communicationMethods"
        @suggestion-accepted="handleSuggestionAccepted"
        @inbound-communication="handleInboundCommunication"
        @last-activity="$emit('last-activity', $event)"
        @pending-change="setPending('communications', $event)"
      />
      <slot name="communication-after"></slot>
    </div>
  </div>
</template>

<script>
import { getLeadsCommunicationMethods } from '@/http'
import LeadCommunicationHistory from '@/views/crm/task-tracker/LeadCommunicationHistory.vue'
import LeadFlowActions from '@/views/crm/task-tracker/LeadFlowActions.vue'
import LeadFollowUpAutomationPanel from '@/views/crm/task-tracker/LeadFollowUpAutomationPanel.vue'
import LeadFlowTimeline from '@/views/crm/task-tracker/LeadFlowTimeline.vue'
import LeadNotesPanel from '@/views/crm/task-tracker/LeadNotesPanel.vue'

export default {
  name: 'LeadFlowWorkspace',
  components: {
    LeadCommunicationHistory,
    LeadFlowActions,
    LeadFollowUpAutomationPanel,
    LeadFlowTimeline,
    LeadNotesPanel
  },
  props: {
    leadId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      communicationMethods: [],
      communicationMethodsLoading: false,
      communicationMethodsError: null,
      communicationMethodsGeneration: 0,
      flowRefreshKey: 0,
      timelineRefreshKey: 0,
      currentStage: null,
      pendingSuggestionReconcile: null,
      pendingSections: {},
      followUpRefreshTimer: null
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.loadCommunicationMethods()
      }
    }
  },
  beforeDestroy() {
    this.communicationMethodsGeneration += 1
    clearTimeout(this.followUpRefreshTimer)
  },
  methods: {
    async loadCommunicationMethods() {
      const generation = this.communicationMethodsGeneration + 1
      this.communicationMethodsGeneration = generation
      this.communicationMethodsLoading = true
      this.communicationMethodsError = null
      try {
        const { data } = await getLeadsCommunicationMethods()
        if (generation !== this.communicationMethodsGeneration) return
        this.communicationMethods = Array.isArray(data) ? data : (data.results || [])
      } catch (error) {
        if (generation === this.communicationMethodsGeneration) {
          this.communicationMethodsError = error
        }
      } finally {
        if (generation === this.communicationMethodsGeneration) {
          this.communicationMethodsLoading = false
        }
      }
    },
    handleFlowLoaded(flow) {
      const nextStage = flow.current_stage || null
      this.currentStage = nextStage
      if (nextStage) this.$emit('stage-change', nextStage)
      if (!this.pendingSuggestionReconcile) return

      const pending = this.pendingSuggestionReconcile
      this.pendingSuggestionReconcile = null
      this.$emit('board-reconcile', {
        ...pending.data,
        previous_stage: pending.previousStage,
        current_stage: nextStage,
        tracker_delta: pending.data.tracker_delta || null
      })
    },
    handleActionComplete(change) {
      this.timelineRefreshKey += 1
      this.refreshFollowUp()
      this.$emit('board-reconcile', change)
    },
    handleActionConflict(change) {
      this.timelineRefreshKey += 1
      this.$emit('board-reconcile', change || {})
    },
    handleSuggestionAccepted(data) {
      const previousStage = data.previous_stage || this.currentStage
      this.timelineRefreshKey += 1
      this.flowRefreshKey += 1
      this.handleInboundCommunication()
      if (data.current_stage) {
        this.$emit('board-reconcile', {
          ...data,
          previous_stage: previousStage
        })
        return
      }
      this.pendingSuggestionReconcile = {
        previousStage,
        data
      }
    },
    async refreshAll() {
      clearTimeout(this.followUpRefreshTimer)
      this.followUpRefreshTimer = null

      const actions = this.$refs.flowActions
      const timeline = this.$refs.timeline
      const notes = this.$refs.notesPanel
      const history = this.$refs.communicationHistory

      await Promise.all([
        this.loadCommunicationMethods(),
        actions ? actions.loadFlow() : Promise.resolve(),
        timeline ? timeline.load(true) : Promise.resolve(),
        notes ? notes.loadNotes() : Promise.resolve(),
        history ? history.refreshAll() : Promise.resolve(),
        this.refreshFollowUp()
      ])
    },
    refreshFollowUp() {
      const panel = this.$refs.followUpPanel
      return panel ? panel.load() : Promise.resolve(null)
    },
    async handleInboundCommunication() {
      const panel = this.$refs.followUpPanel
      const followUp = panel ? await panel.refreshAfterInbound() : null
      if (!followUp) return
      this.$emit('board-reconcile', {
        previous_stage: this.currentStage,
        current_stage: this.currentStage
      })
    },
    scheduleFollowUpRefresh() {
      clearTimeout(this.followUpRefreshTimer)
      this.followUpRefreshTimer = setTimeout(() => {
        this.followUpRefreshTimer = null
        this.handleInboundCommunication()
      }, 2000)
    },
    setPending(section, pending) {
      if (pending) this.$set(this.pendingSections, section, true)
      else this.$delete(this.pendingSections, section)
      this.$emit('pending-change', Object.keys(this.pendingSections).length > 0)
    },
    reset() {
      clearTimeout(this.followUpRefreshTimer)
      this.followUpRefreshTimer = null
      this.communicationMethodsGeneration += 1
      this.communicationMethods = []
      this.communicationMethodsLoading = false
      this.communicationMethodsError = null
      this.flowRefreshKey = 0
      this.timelineRefreshKey = 0
      this.currentStage = null
      this.pendingSuggestionReconcile = null
      this.pendingSections = {}
      this.$emit('pending-change', false)
    }
  }
}
</script>

<style scoped>
.lead-flow-workspace {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(440px, 1.25fr);
  align-items: start;
  gap: 16px;
  padding: 20px 24px 32px;
}

.lead-flow-workspace__main {
  display: grid;
  min-width: 0;
  grid-column: 1 / 3;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 16px;
}

.lead-flow-workspace__main > * {
  grid-column: 1 / -1;
}

.lead-flow-workspace__overview {
  min-width: 0;
}

.lead-flow-workspace__operations {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 16px;
}

.lead-flow-workspace__operations > .lead-flow-timeline {
  grid-column: 1;
  grid-row: 1;
}

.lead-flow-workspace__operations > .lead-follow-up-panel {
  grid-column: 2;
  grid-row: 1 / span 2;
}

.lead-flow-workspace__operations > .lead-notes-panel {
  grid-column: 1;
  grid-row: 2;
}

.lead-flow-workspace__side {
  display: grid;
  min-width: 0;
  grid-column: 3;
  align-content: start;
  gap: 16px;
}

.lead-flow-workspace__dictionary-error {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e4c4bf;
  border-radius: 10px;
  background: #fff7f5;
  color: #a34d46;
  font-size: 12px;
}

.lead-flow-workspace__dictionary-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 700;
  cursor: pointer;
}

@media (max-width: 1180px) {
  .lead-flow-workspace {
    grid-template-columns: 1fr;
  }

  .lead-flow-workspace__main,
  .lead-flow-workspace__side {
    grid-column: auto;
  }
}

@media (max-width: 900px) {
  .lead-flow-workspace__main,
  .lead-flow-workspace__operations {
    grid-template-columns: 1fr;
  }

  .lead-flow-workspace__operations > .lead-flow-timeline,
  .lead-flow-workspace__operations > .lead-follow-up-panel,
  .lead-flow-workspace__operations > .lead-notes-panel {
    grid-column: auto;
    grid-row: auto;
  }
}

@media (max-width: 600px) {
  .lead-flow-workspace {
    padding-right: 14px;
    padding-left: 14px;
  }
}
</style>
